import * as THREE from 'three';
import { loadGlb } from '../../../assets/loadGlb';
import { DinoActor, type FaunaSense } from '../../../dinos/DinoActor';
import { PTERA_KIT, REX_KIT } from '../../../dinos/kits';
import { nearestInteract, queryInteract, type InteractTarget, type Xyz } from '../../../interact/Interact';
import type { PhysicsWorld } from '../../../physics/PhysicsWorld';
import { parseEnding, type EndingId } from '../../../systems/Ending';
import { setPrompt } from '../../../ui/Prompt';
import { hasAllKeys } from '../a06/Underground';
import { VZ_NODES } from '../a05/VolcanicZone';

export const MATINS_GLB = '/models/env/env_matins.glb';
export const PLINTH_GLB: Record<EndingId, string> = {
  seal: '/models/env/env_ending_plinth_seal.glb',
  carry: '/models/env/env_ending_plinth_carry.glb',
  feed: '/models/env/env_ending_plinth_feed.glb',
  burn: '/models/env/env_ending_plinth_burn.glb',
};

/** Authored south face looks at the nave. Interact volumes stay on LW_NODES. */
const PLINTH_YAW: Record<EndingId, number> = {
  seal: Math.PI / 2,
  carry: 0,
  feed: -Math.PI / 2,
  burn: Math.PI,
};

export type LostWorldPoint = { x: number; y: number; z: number };

export type { EndingId } from '../../../systems/Ending';

export type LostWorldHook = {
  prompt?: string;
  ending?: EndingId | null;
  flags?: { knowMatins?: boolean; stampMatins?: boolean; carryFlask?: boolean };
  dinos?: { id: string; x: number; y: number; z: number; hp: number; kind: string; taxon: string }[];
};

/** Coast extract for CARRY. Crash Coast wreck-south beacon. */
export const CC_BEACON = { x: 10, y: 0.9, z: 10 };
const BEACON_RANGE = 2.6;

/** Documented verb lock: KNOW.MATINS + STAMP.MATINS. Keys only open the vale. */
export function canCommitEnding(flags?: { knowMatins?: boolean; stampMatins?: boolean }): boolean {
  return !!flags?.knowMatins && !!flags?.stampMatins;
}

/** Vale streams once the three keys are held and the player is north of the gate. */
export const LW_STREAM_Z = -220;
/** Area slug flips past the gate fringe, not on the socket wall. */
export const LW_AREA_Z = -230;
export const LW_FLOOR_Y = 8;
/** Capsule-safe stand on the vale slab (floor at y=8). */
export const LW_SPAWN: LostWorldPoint = { x: 0, y: 10, z: -240 };

export const LW_NODES: Record<string, LostWorldPoint> = {
  'LW-VALE': { x: 0, y: LW_FLOOR_Y, z: -240 },
  'LW-SPAWN': LW_SPAWN,
  'LW-REX': { x: 6, y: LW_FLOOR_Y, z: -250 },
  'LW-PTERA': { x: -14, y: LW_FLOOR_Y + 6.6, z: -236 },
  'LW-SEAL': { x: -8, y: 9.1, z: -248 },
  'LW-CARRY': { x: 0, y: 9.1, z: -256 },
  'LW-FEED': { x: 8, y: 9.1, z: -248 },
  'LW-BURN': { x: 0, y: 9.1, z: -236 },
  'LW-KNOW': { x: 0, y: 9.1, z: -245 },
  'LW-STAMP': { x: 1.4, y: 9.1, z: -247 },
};

export const LW_FOG_COLOR = 0x6a8860;
export const LW_FOG_DENSITY = 0.016;
export const LW_FOG_NIGHT = 0.026;

const GROUND_HALF_Y = 0.5;
const REX_BOUNDS = { minX: -16, maxX: 16, minZ: -262, maxZ: -232 };
const PTERA_BOUNDS = { minX: -18, maxX: 18, minZ: -262, maxZ: -228 };
const _look = new THREE.Vector3();

const ENDING_PROMPT: Record<EndingId, string> = {
  seal: 'SEAL',
  carry: 'CARRY',
  feed: 'FEED',
  burn: 'BURN',
};

type ValeMats = {
  grass: THREE.MeshStandardMaterial;
  turf: THREE.MeshStandardMaterial;
  blade: THREE.MeshStandardMaterial;
  mist: THREE.MeshStandardMaterial;
  stone: THREE.MeshStandardMaterial;
  nave: THREE.MeshStandardMaterial;
  rust: THREE.MeshStandardMaterial;
  seal: THREE.MeshStandardMaterial;
  carry: THREE.MeshStandardMaterial;
  feed: THREE.MeshStandardMaterial;
  burn: THREE.MeshStandardMaterial;
};

function loadMap(url: string, rx: number, ry: number): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

function makeMats(): ValeMats {
  return {
    grass: new THREE.MeshStandardMaterial({
      color: 0x5a7844,
      map: loadMap('/tex/fern.jpg', 4, 4),
      roughness: 0.92,
      metalness: 0.02,
    }),
    turf: new THREE.MeshStandardMaterial({
      color: 0x6a8848,
      map: loadMap('/tex/sand.jpg', 5, 5),
      roughness: 0.9,
      metalness: 0.02,
    }),
    blade: new THREE.MeshStandardMaterial({
      color: 0x7a9a52,
      map: loadMap('/tex/fern.jpg', 1, 1),
      roughness: 0.82,
      metalness: 0.02,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.28,
    }),
    mist: new THREE.MeshStandardMaterial({
      color: 0x8aaa88,
      roughness: 0.95,
      metalness: 0,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
    stone: new THREE.MeshStandardMaterial({
      color: 0x6a6860,
      map: loadMap('/tex/rock.jpg', 3, 3),
      roughness: 0.9,
      metalness: 0.06,
    }),
    nave: new THREE.MeshStandardMaterial({
      color: 0x8a8678,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.84,
      metalness: 0.1,
    }),
    rust: new THREE.MeshStandardMaterial({
      color: 0x6a4a32,
      map: loadMap('/tex/wreck.jpg', 1, 1),
      roughness: 0.7,
      metalness: 0.26,
    }),
    seal: new THREE.MeshStandardMaterial({ color: 0x4a6a88, roughness: 0.5, metalness: 0.2, emissive: 0x102030, emissiveIntensity: 0.35 }),
    carry: new THREE.MeshStandardMaterial({ color: 0x8a7a48, roughness: 0.5, metalness: 0.2, emissive: 0x302010, emissiveIntensity: 0.35 }),
    feed: new THREE.MeshStandardMaterial({ color: 0x7a3a3a, roughness: 0.5, metalness: 0.16, emissive: 0x301010, emissiveIntensity: 0.4 }),
    burn: new THREE.MeshStandardMaterial({ color: 0x8a4a20, roughness: 0.45, metalness: 0.18, emissive: 0x401808, emissiveIntensity: 0.55 }),
  };
}

export function inLostWorld(
  z: number,
  y: number,
  flags?: { keyA?: boolean; keyB?: boolean; keyC?: boolean },
): boolean {
  return hasAllKeys(flags) && z < LW_AREA_Z && y >= 0;
}

/**
 * MATINS vale north of the three-key gate. Gated on keyA+keyB+keyC
 * (`__DS.give('keys')` for QA). Dressing streams at z < -220.
 */
export class LostWorld {
  streamed = false;
  prompt = '';
  ending: EndingId | null = null;
  knowMatins = false;
  stampMatins = false;
  carryFlask = false;
  private pulseActive = false;
  hunter: DinoActor | null = null;
  scout: DinoActor | null = null;
  private groundReady = false;
  private verbs: InteractTarget[] = [];

  extendGround(physics: PhysicsWorld): void {
    if (this.groundReady) {
      return;
    }
    // Raised vale north of the volcanic slab. Top face at y = 8.
    physics.addStaticCuboid(28, GROUND_HALF_Y, 24, 0, LW_FLOOR_Y - GROUND_HALF_Y, -248);
    this.groundReady = true;
  }

  /** Load dressing the first time the player holds all keys and is past the gate. */
  sync(
    scene: THREE.Scene,
    physics: PhysicsWorld,
    z: number,
    flags?: { keyA?: boolean; keyB?: boolean; keyC?: boolean },
  ): void {
    this.extendGround(physics);
    if (this.streamed || !hasAllKeys(flags) || z >= LW_STREAM_Z) {
      return;
    }
    this.build(scene, physics);
  }

  tick(
    dt: number,
    camera: THREE.Camera,
    player: Xyz,
    use: boolean,
    sense?: FaunaSense,
    pulseActive = false,
  ): boolean {
    this.pulseActive = pulseActive;
    if (this.tryExtract(player) && use) {
      return true;
    }
    if (!this.streamed) {
      return false;
    }
    this.hunter?.tick(dt, player, sense);
    this.scout?.tick(dt, player, sense);
    const hit = this.query(camera, player);
    this.applyPrompt(hit);
    if (!use) {
      return hit !== null;
    }
    return this.use(hit);
  }

  forceUse(camera: THREE.Camera, player: Xyz, pulseActive = false): boolean {
    this.pulseActive = pulseActive;
    if (this.tryExtract(player)) {
      return true;
    }
    if (!this.streamed) {
      return false;
    }
    const hit = this.query(camera, player) ?? this.nearest(player);
    const ok = this.use(hit);
    this.applyPrompt(this.query(camera, player) ?? this.nearest(player));
    return ok;
  }

  commit(id: EndingId): boolean {
    if (this.ending) {
      return false;
    }
    this.ending = id;
    return true;
  }

  /** CARRY lock: flask leaves via the coast beacon, not the vale plinth. */
  tryExtract(player: Xyz): boolean {
    if (this.ending || !this.carryFlask || !canCommitEnding(this)) {
      return false;
    }
    if (Math.hypot(player.x - CC_BEACON.x, player.z - CC_BEACON.z) > BEACON_RANGE) {
      return false;
    }
    this.ending = 'carry';
    return true;
  }

  syncPrompt(camera: THREE.Camera, player: Xyz): void {
    if (this.ending) {
      return;
    }
    if (
      this.carryFlask &&
      canCommitEnding(this) &&
      Math.hypot(player.x - CC_BEACON.x, player.z - CC_BEACON.z) <= BEACON_RANGE
    ) {
      setPrompt('E — Extract the sample');
      return;
    }
    const cinder = VZ_NODES['VZ-CINDER'] ?? { x: -4, z: -196 };
    if (
      canCommitEnding(this) &&
      Math.hypot(player.x - cinder.x, player.z - cinder.z) <= 3.4
    ) {
      setPrompt('E — Ignite the Meridian charge');
      return;
    }
    if (!this.streamed) {
      return;
    }
    this.applyPrompt(this.query(camera, player));
  }

  restore(flags: {
    ending?: unknown;
    knowMatins?: unknown;
    stampMatins?: unknown;
    carryFlask?: unknown;
  }): void {
    const next = parseEnding(flags.ending);
    if (next) {
      this.ending = next;
    }
    if (flags.knowMatins === true) {
      this.knowMatins = true;
    }
    if (flags.stampMatins === true) {
      this.stampMatins = true;
    }
    if (flags.carryFlask === true) {
      this.carryFlask = true;
    }
  }

  /** QA / documented walk: `__DS.give('matins')` stamps KNOW + STAMP. */
  grantLore(id: string): void {
    if (id === 'knowMatins' || id === 'matins') {
      this.knowMatins = true;
    }
    if (id === 'stampMatins' || id === 'matins') {
      this.stampMatins = true;
    }
  }

  publish(hook: LostWorldHook): void {
    hook.ending = this.ending;
    if (hook.flags) {
      hook.flags.knowMatins = this.knowMatins;
      hook.flags.stampMatins = this.stampMatins;
      hook.flags.carryFlask = this.carryFlask;
    }
    const extra = [this.hunter, this.scout].filter((dino): dino is DinoActor => dino !== null);
    if (extra.length > 0) {
      hook.dinos = [...(hook.dinos ?? []), ...extra.map((dino) => dino.snap())];
    }
    if (this.prompt) {
      hook.prompt = this.prompt;
    }
  }

  applyFog(scene: THREE.Scene, night: boolean): void {
    if (scene.fog instanceof THREE.FogExp2) {
      scene.fog.density = night ? LW_FOG_NIGHT : LW_FOG_DENSITY;
      scene.fog.color.setHex(LW_FOG_COLOR);
    }
    if (scene.background instanceof THREE.Color) {
      scene.background.setHex(night ? 0x243028 : LW_FOG_COLOR);
    }
  }

  private query(camera: THREE.Camera, player: Xyz) {
    camera.getWorldDirection(_look);
    return queryInteract(player, _look, player, this.verbs);
  }

  private nearest(player: Xyz) {
    return nearestInteract(player, this.verbs);
  }

  private use(hit: { target: InteractTarget } | null): boolean {
    if (this.ending) {
      return false;
    }
    const id = hit?.target.id;
    if (id === 'LW-KNOW') {
      this.knowMatins = true;
      return true;
    }
    if (id === 'LW-STAMP') {
      if (!this.knowMatins) {
        setPrompt('Read the plates first');
        return true;
      }
      this.stampMatins = true;
      return true;
    }
    if (id === 'LW-SEAL') {
      if (!canCommitEnding(this)) {
        setPrompt(this.knowMatins ? 'Stamp the plates first' : 'Read the plates first');
        return true;
      }
      this.ending = 'seal';
      return true;
    }
    if (id === 'LW-CARRY') {
      if (!canCommitEnding(this)) {
        setPrompt(this.knowMatins ? 'Stamp the plates first' : 'Read the plates first');
        return true;
      }
      this.carryFlask = true;
      setPrompt('Take the sample to the coast beacon');
      return true;
    }
    if (id === 'LW-FEED') {
      if (!canCommitEnding(this)) {
        setPrompt(this.knowMatins ? 'Stamp the plates first' : 'Read the plates first');
        return true;
      }
      if (!this.pulseActive) {
        setPrompt('Wait for the Pulse');
        return true;
      }
      this.ending = 'feed';
      return true;
    }
    if (id === 'LW-BURN') {
      setPrompt('Ignite the charge at Mount Cinder');
      return true;
    }
    return false;
  }

  private applyPrompt(hit: { target: InteractTarget } | null): void {
    if (hit?.target.id === 'LW-KNOW') {
      hit.target.prompt = this.knowMatins ? 'Plates read' : 'E — Read the plates';
    }
    if (hit?.target.id === 'LW-STAMP') {
      hit.target.prompt = this.stampMatins
        ? this.knowMatins
          ? 'Plates stamped'
          : 'Read the plates first'
        : this.knowMatins
          ? 'E — Stamp the plates'
          : 'Read the plates first';
    }
    this.prompt = hit?.target.prompt ?? '';
    if (this.prompt) {
      setPrompt(this.prompt);
    }
  }

  private build(scene: THREE.Scene, physics: PhysicsWorld): void {
    this.streamed = true;
    const root = new THREE.Group();
    tag(root, 'The Lost World');
    root.userData.area = 'lost_world';
    scene.add(root);

    const mats = makeMats();
    addVale(root, mats);
    addGrass(root, mats);
    addMist(root, mats);
    addMenhirs(root, physics, mats);
    addNave(root, physics, mats);
    this.addVerbs(root, mats);
    this.addLore(root, mats);
    this.spawnHunter(root);

    const sun = new THREE.PointLight(0xffe0a0, 1.2, 42, 1.4);
    sun.position.set(0, 16, -244);
    sun.name = 'LW-SUN';
    root.add(sun);
  }

  private addVerbs(root: THREE.Group, mats: ValeMats): void {
    const specs: [EndingId, THREE.MeshStandardMaterial][] = [
      ['seal', mats.seal],
      ['carry', mats.carry],
      ['feed', mats.feed],
      ['burn', mats.burn],
    ];
    for (const [ending, mat] of specs) {
      const id = `LW-${ending.toUpperCase()}` as const;
      const pos = LW_NODES[id] ?? { x: 0, y: 9.1, z: -248 };
      const plinth = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.7, 0.7, 8), mat);
      plinth.position.set(pos.x, LW_FLOOR_Y + 0.35, pos.z);
      plinth.castShadow = true;
      tag(plinth, id);
      root.add(plinth);
      this.verbs.push({
        id,
        prompt: ENDING_PROMPT[ending],
        position: { ...pos },
        radius: 1.1,
        live: true,
      });
      void dressPlinth(root, plinth, ending, mats);
    }
  }

  private addLore(root: THREE.Group, mats: ValeMats): void {
    const knowAt = LW_NODES['LW-KNOW'] ?? { x: 0, y: 9.1, z: -245 };
    const stampAt = LW_NODES['LW-STAMP'] ?? { x: 1.4, y: 9.1, z: -247 };
    const plaque = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.08, 0.7), mats.stone);
    plaque.position.set(knowAt.x, LW_FLOOR_Y + 1.15, knowAt.z);
    tag(plaque, 'LW-KNOW');
    root.add(plaque);
    const press = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.7, 0.55), mats.rust);
    press.position.set(stampAt.x, LW_FLOOR_Y + 0.35, stampAt.z);
    tag(press, 'LW-STAMP');
    root.add(press);
    this.verbs.push(
      {
        id: 'LW-KNOW',
        prompt: 'E — Read the plates',
        position: { ...knowAt },
        radius: 1.2,
        live: true,
      },
      {
        id: 'LW-STAMP',
        prompt: 'E — Stamp the plates',
        position: { ...stampAt },
        radius: 1.2,
        live: true,
      },
    );
  }

  private spawnHunter(root: THREE.Group): void {
    const spot = LW_NODES['LW-REX'] ?? { x: 6, y: LW_FLOOR_Y, z: -250 };
    this.hunter = new DinoActor('LW-REX', REX_KIT, spot.x, spot.z, REX_BOUNDS, LW_FLOOR_Y);
    root.add(this.hunter.mesh);
    const pteraSpot = LW_NODES['LW-PTERA'] ?? { x: -14, y: LW_FLOOR_Y + 6.6, z: -236 };
    this.scout = new DinoActor('LW-PTERA', PTERA_KIT, pteraSpot.x, pteraSpot.z, PTERA_BOUNDS, LW_FLOOR_Y);
    root.add(this.scout.mesh);
  }
}

function addVale(root: THREE.Group, mats: ValeMats): void {
  const geo = new THREE.PlaneGeometry(56, 48, 16, 14);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i) - 248;
      let y = Math.sin(x * 0.12 + z * 0.1) * 0.05;
      if (Math.hypot(x, z + 240) < 4 || Math.hypot(x, z + 248) < 5) {
        y = 0;
      }
      pos.setY(i, 0.02 + y);
    }
    pos.needsUpdate = true;
  }
  geo.computeVertexNormals();
  const floor = new THREE.Mesh(geo, mats.grass);
  floor.position.set(0, LW_FLOOR_Y, -248);
  floor.receiveShadow = true;
  tag(floor, 'LW-VALE');
  root.add(floor);

  const pads: [number, number, number][] = [
    [-14, -238, 4.2],
    [14, -238, 4.0],
    [-16, -258, 4.4],
    [16, -258, 4.1],
    [0, -266, 3.6],
  ];
  for (let i = 0; i < pads.length; i++) {
    const p = pads[i];
    if (!p) {
      continue;
    }
    const turf = new THREE.Mesh(new THREE.CircleGeometry(p[2], 12), mats.turf);
    turf.rotation.x = -Math.PI / 2;
    turf.position.set(p[0], LW_FLOOR_Y + 0.03, p[1]);
    turf.receiveShadow = true;
    tag(turf, `LW-TURF-${String(i + 1).padStart(2, '0')}`);
    root.add(turf);
  }
}

function addGrass(root: THREE.Group, mats: ValeMats): void {
  const clumps: [number, number][] = [
    [-18, -236],
    [18, -236],
    [-20, -250],
    [20, -250],
    [-16, -262],
    [16, -262],
    [-10, -232],
    [10, -232],
    [-22, -244],
    [22, -244],
    [-12, -268],
    [12, -268],
    [4, -264],
    [-4, -232],
  ];
  const count = clumps.length;
  const per = 5;
  const blades = new THREE.InstancedMesh(new THREE.PlaneGeometry(0.45, 0.85), mats.blade, count * per);
  blades.name = 'LW-GRASS';
  root.add(blades);
  const dummy = new THREE.Object3D();
  let fi = 0;
  for (let i = 0; i < count; i++) {
    const c = clumps[i];
    if (!c) {
      continue;
    }
    const [x, z] = c;
    for (let k = 0; k < per; k++) {
      dummy.position.set(x + (k - 2) * 0.16, LW_FLOOR_Y + 0.4, z + (k % 2) * 0.1);
      dummy.rotation.set(0.05, k * 0.6 + i * 0.15, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      blades.setMatrixAt(fi, dummy.matrix);
      fi += 1;
    }
  }
  blades.instanceMatrix.needsUpdate = true;
  blades.computeBoundingSphere();
}

function addMist(root: THREE.Group, mats: ValeMats): void {
  const sheets: [number, number, number, number][] = [
    [-12, 1.2, -234, 10],
    [12, 1.4, -234, 10],
    [0, 1.6, -262, 14],
    [-20, 1.3, -252, 8],
    [20, 1.3, -252, 8],
  ];
  for (let i = 0; i < sheets.length; i++) {
    const s = sheets[i];
    if (!s) {
      continue;
    }
    const [x, y, z, w] = s;
    const mist = new THREE.Mesh(new THREE.PlaneGeometry(w, 2.4), mats.mist);
    mist.position.set(x, LW_FLOOR_Y + y, z);
    mist.renderOrder = 4;
    tag(mist, `LW-MIST-${String(i + 1).padStart(2, '0')}`);
    root.add(mist);
  }
}

function addMenhirs(root: THREE.Group, physics: PhysicsWorld, mats: ValeMats): void {
  const stones: [number, number, number][] = [
    [-18, -242, 2.4],
    [18, -242, 2.6],
    [-18, -254, 2.2],
    [18, -254, 2.5],
    [-10, -266, 2.1],
    [10, -266, 2.3],
  ];
  for (let i = 0; i < stones.length; i++) {
    const s = stones[i];
    if (!s) {
      continue;
    }
    const [x, z, h] = s;
    const stone = new THREE.Mesh(new THREE.BoxGeometry(0.7, h, 0.45), mats.stone);
    stone.position.set(x, LW_FLOOR_Y + h * 0.5, z);
    stone.rotation.y = i * 0.35;
    stone.castShadow = true;
    stone.receiveShadow = true;
    tag(stone, `LW-MENHIR-${String(i + 1).padStart(2, '0')}`);
    root.add(stone);
    physics.addStaticCuboid(0.35, h * 0.5, 0.22, x, LW_FLOOR_Y + h * 0.5, z);
  }
}

function addNave(root: THREE.Group, physics: PhysicsWorld, mats: ValeMats): void {
  const nave = new THREE.Group();
  tag(nave, 'MATINS');
  nave.position.set(0, LW_FLOOR_Y, -248);
  root.add(nave);

  const body = new THREE.Mesh(new THREE.BoxGeometry(6.4, 4.2, 8.2), mats.nave);
  body.position.y = 2.1;
  body.castShadow = true;
  body.receiveShadow = true;
  tag(body, 'LW-NAVE');
  nave.add(body);

  const arch = new THREE.Mesh(new THREE.BoxGeometry(3.2, 3.2, 0.4), mats.stone);
  arch.position.set(0, 1.8, 4.2);
  tag(arch, 'LW-NAVE-ARCH');
  nave.add(arch);

  const plaque = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.28, 0.06), mats.rust);
  plaque.position.set(0, 3.4, 4.14);
  tag(plaque, 'LW-NAVE-PLAQUE');
  nave.add(plaque);

  physics.addStaticCuboid(3.2, 2.1, 4.1, 0, LW_FLOOR_Y + 2.1, -248);
  void dressNave(nave, mats);
}

/** Swap a verb cylinder for the authored plinth. Keeps the primitive if the file is missing. */
async function dressPlinth(
  root: THREE.Group,
  stub: THREE.Mesh,
  ending: EndingId,
  mats: ValeMats,
): Promise<void> {
  try {
    const hero = await loadGlb(PLINTH_GLB[ending]);
    hero.name = `LW-${ending.toUpperCase()}-HERO`;
    hero.position.set(stub.position.x, LW_FLOOR_Y, stub.position.z);
    hero.rotation.y = PLINTH_YAW[ending];
    const rock = mats.nave.map;
    const wreck = mats.rust.map;
    const verb = mats[ending];
    hero.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      obj.castShadow = true;
      obj.receiveShadow = true;
      const slots = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of slots) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          continue;
        }
        const slug = mat.name.toLowerCase();
        if (rock && (slug.includes('concrete') || slug.includes('stone'))) {
          mat.map = rock;
          mat.needsUpdate = true;
        }
        if (wreck && (slug.includes('rust') || slug.includes('enamel'))) {
          mat.map = wreck;
          mat.needsUpdate = true;
        }
        if (slug.includes('fold') || slug.includes('gold') || slug.includes('blood') || slug.includes('ember')) {
          mat.color.copy(verb.color);
          mat.emissive.copy(verb.emissive);
          mat.emissiveIntensity = Math.max(mat.emissiveIntensity, verb.emissiveIntensity);
          mat.roughness = verb.roughness;
          mat.metalness = verb.metalness;
          mat.needsUpdate = true;
        }
      }
    });
    stub.visible = false;
    root.add(hero);
  } catch {
    /* keep cylinder plinth */
  }
}

/** Swap the box nave for the authored MATINS GLB. Keeps primitives if the file is missing. */
async function dressNave(nave: THREE.Group, mats: ValeMats): Promise<void> {
  try {
    const hero = await loadGlb(MATINS_GLB);
    hero.name = 'LW-NAVE-HERO';
    const rock = mats.nave.map;
    const wreck = mats.rust.map;
    hero.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      obj.castShadow = true;
      obj.receiveShadow = true;
      const slots = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of slots) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          continue;
        }
        const slug = mat.name.toLowerCase();
        if (rock && (slug.includes('concrete') || slug.includes('stone'))) {
          mat.map = rock;
          mat.needsUpdate = true;
        }
        if (wreck && slug.includes('rust')) {
          mat.map = wreck;
          mat.needsUpdate = true;
        }
        if (slug.includes('fold')) {
          mat.emissive.setHex(0x7eb8a8);
          mat.emissiveIntensity = Math.max(mat.emissiveIntensity, 1.4);
          mat.needsUpdate = true;
        }
      }
    });
    for (const child of [...nave.children]) {
      if (child.name === 'LW-NAVE' || child.name === 'LW-NAVE-ARCH' || child.name === 'LW-NAVE-PLAQUE') {
        child.visible = false;
      }
    }
    nave.add(hero);
  } catch {
    /* keep primitive nave */
  }
}

function tag(obj: THREE.Object3D, id: string): void {
  obj.name = id;
  obj.userData.id = id;
}
